import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Paperclip, Send, Archive, Download, X, Users } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C, MobileSheet} from './MobileShell';
import MessageDeliveryStatus from '@/components/messages/MessageDeliveryStatus';
import {
  conversationContext, conversationListTitle, indexContactsByUser, isGroupConversation, personName,
} from './conversationTitle';

function fileKind(att) {
  const mime = String(att?.mime_type || att?.content_type || '').toLowerCase();
  const name = String(att?.filename || '');
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(name)) return 'Bild';
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) return 'PDF';
  if (mime.startsWith('audio/') || /\.(mp3|m4a|wav|aac|ogg)$/i.test(name)) return 'Audio';
  if (/word|wordprocessingml|\.docx?$/i.test(`${mime} ${name}`)) return 'Word';
  return 'Datei';
}

function fileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) {
    const mb = Math.round((n / (1024 * 1024)) * 10) / 10;
    return `${mb.toFixed(1).replace('.', ',')} MB`;
  }
  if (n >= 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n > 0) return `${n} B`;
  return '';
}

function AttachmentCard({ att, onDownload }) {
  const image = fileKind(att) === 'Bild';
  const [preview, setPreview] = useState('');
  useEffect(() => {
    if (!image) return undefined;
    let cancelled = false;
    let url = '';
    mapi.get(`/messages/attachments/${att.id}`, { responseType: 'blob' })
      .then((r) => {
        if (cancelled || typeof URL.createObjectURL !== 'function') return;
        url = URL.createObjectURL(r.data);
        setPreview(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    };
  }, [att.id, image]);
  const meta = [fileKind(att), fileSize(att.size_bytes)].filter(Boolean).join(' · ');
  return (
    <button type="button" onClick={() => onDownload(att)}
            data-testid={`mobile-att-${att.id}`}
            className="mt-1.5 flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left"
            style={{ background: C.card, border: `1px solid ${C.border}`, color: C.text }}>
      {preview ? (
        <img src={preview} alt="" data-testid={`mobile-att-preview-${att.id}`}
             className="h-12 w-12 shrink-0 rounded-lg object-cover" />
      ) : (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold"
              style={{ background: C.rose, color: C.red }}>
          {fileKind(att) === 'PDF' ? 'PDF' : <Download className="w-4 h-4" />}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold">{att.filename}</span>
        <span className="block text-[10px]" style={{ color: C.dim }}>{meta}</span>
      </span>
      <Download className="w-4 h-4 shrink-0" style={{ color: C.red }} />
    </button>
  );
}

export default function MobileChat() {
  const { convId } = useParams();
  const { me } = useMobile();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPeople, setShowPeople] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [contactsByUser, setContactsByUser] = useState({});
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, m, people] = await Promise.all([
        mapi.get(`/messages/conversations/${convId}`),
        mapi.get(`/messages/conversations/${convId}/messages`),
        mapi.get('/app/contacts').catch(() => ({ data: [] })),
      ]);
      setConv(c.data);
      setContactsByUser(indexContactsByUser(people.data || []));
      setMsgs(m.data || []);
      const incoming = (m.data || []).filter((x) => x.sender?.id !== me?.id && !x.deleted_at);
      await Promise.all(incoming.map(async (message) => {
        await mapi.post(`/messages/messages/${message.id}/delivered`).catch(() => {});
        await mapi.post(`/messages/messages/${message.id}/read`).catch(() => {});
      }));
      await mapi.post(`/messages/conversations/${convId}/personal-unread`, { unread: false }).catch(() => {});
    } catch (e) {
      toast.error('Chat konnte nicht geladen werden');
    }
  }, [convId, me]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim() && !files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('body', text);
      files.forEach((f) => fd.append('attachments', f));
      await mapi.post(`/messages/conversations/${convId}/messages`, fd);
      setText('');
      setFiles([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    try {
      await mapi.post(`/messages/conversations/${convId}/archive`);
      toast.success('Chat archiviert');
    } catch (e) { toast.error('Archivieren fehlgeschlagen'); }
  };

  const closeViewer = () => {
    if (viewer?.url) URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  const downloadAtt = async (att) => {
    try {
      const r = await mapi.get(`/messages/attachments/${att.id}`, { responseType: 'blob' });
      const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: att.mime_type || '' });
      const url = URL.createObjectURL(blob);
      if (viewer?.url) URL.revokeObjectURL(viewer.url);
      setViewer({ att, url, blob });
    } catch (e) {
      toast.error('Datei konnte nicht geladen werden');
    }
  };

  const saveViewer = async () => {
    if (!viewer) return;
    const file = new File([viewer.blob], viewer.att.filename || 'datei', {
      type: viewer.att.mime_type || viewer.blob.type || 'application/octet-stream',
    });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: file.name });
        return;
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
    const a = document.createElement('a');
    a.href = viewer.url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const fmtTime = (d) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  const header = conversationListTitle(conv);
  const context = conversationContext(conv, contactsByUser);
  const letters = header.split(/[\s,–-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
  const group = isGroupConversation(conv);

  return (
    <MobileShell bottomPad={false}>
      <div className="box-border flex min-h-0 flex-1 flex-col overflow-hidden">
      <MobileHeader
        title={header}
        backTo="/m/messages"
        leading={(
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                style={{ background: 'rgba(255,255,255,0.16)', color: '#fff' }}
                data-testid="mobile-chat-mark">
            {group ? <Users className="h-4 w-4" /> : letters}
          </span>
        )}
        subtitle={(
          <button
            type="button"
            onClick={() => setShowPeople(true)}
            data-testid="mobile-chat-recipients"
            className="block w-full truncate text-left text-xs font-medium"
            style={{ color: 'rgba(255,255,255,0.88)' }}
          >
            {context || 'Empfänger'}
          </button>
        )}
        right={(
          <button onClick={archive} data-testid="mobile-chat-archive" className="p-1.5" style={{ color: '#fff' }}>
            <Archive className="w-5 h-5" />
          </button>
        )}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="mobile-chat-thread">
        {msgs.map((m) => {
          const mine = m.sender?.id === me?.id;
          const deleted = !!m.deleted_at;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                 data-testid={`mobile-msg-${m.id}`}>
              <div className="max-w-[80%] rounded-2xl px-3.5 py-2.5"
                   style={{ background: mine ? C.rose : '#F3F4F6', color: C.text, border: `1px solid ${C.border}` }}>
                {!mine && <p className="text-[11px] font-semibold mb-0.5" style={{ color: C.red }}>{m.sender?.full_name || m.sender?.username}</p>}
                {deleted ? (
                  <p className="text-sm italic opacity-70">Nachricht gelöscht</p>
                ) : (
                  <>
                    {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                    {(m.attachments || []).map((a) => (
                      <AttachmentCard key={a.id} att={a} onDownload={downloadAtt} />
                    ))}
                  </>
                )}
                <div className="text-[10px] mt-1 flex items-center justify-end gap-1.5" style={{ color: C.dim }}>
                  <span>{fmtTime(m.created_at)}</span>
                  {mine && !deleted && <MessageDeliveryStatus message={m} variant="app" />}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {viewer && (
        <div className="shrink-0 border-t px-3 py-2 space-y-2" data-testid="mobile-att-viewer"
             style={{ background: C.card, borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-xs font-semibold">{viewer.att.filename}</p>
            <button type="button" onClick={saveViewer} className="text-xs font-bold" style={{ color: C.red }}
                    data-testid="mobile-att-save">Öffnen</button>
            <button type="button" onClick={closeViewer} aria-label="Anhang schließen"><X className="w-4 h-4" /></button>
          </div>
          {fileKind(viewer.att) === 'Bild' && (
            <img src={viewer.url} alt={viewer.att.filename} className="max-h-[50vh] w-full object-contain" />
          )}
          {fileKind(viewer.att) === 'PDF' && (
            <iframe title={viewer.att.filename} src={viewer.url} className="h-[50vh] w-full rounded-lg bg-white" />
          )}
          {fileKind(viewer.att) === 'Audio' && (
            <audio controls src={viewer.url} className="w-full" data-testid="mobile-att-audio" />
          )}
          {fileKind(viewer.att) !== 'Bild' && fileKind(viewer.att) !== 'PDF' && fileKind(viewer.att) !== 'Audio' && (
            <p className="text-xs" style={{ color: C.dim }}>{fileKind(viewer.att)} · zum Öffnen oder Teilen antippen</p>
          )}
        </div>
      )}

      <div className="shrink-0 px-3 py-2.5" data-testid="mobile-chat-composer"
           style={{ background: C.card, borderTop: `1px solid ${C.border}` }}>
        {files.length > 0 && (
          <p className="text-xs mb-1.5 px-1" style={{ color: C.dim }}>
            📎 {files.map((f) => f.name).join(', ')}
            <button className="ml-2 underline" onClick={() => setFiles([])}>entfernen</button>
          </p>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp"
                 onChange={(e) => setFiles([...e.target.files])} data-testid="mobile-chat-file-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="mobile-chat-attach"
                  className="p-2 rounded-full" style={{ color: C.dim }}>
            <Paperclip className="w-5 h-5" />
          </button>
          <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Nachricht schreiben…" data-testid="mobile-chat-input"
                    className="flex-1 rounded-2xl px-4 py-2.5 text-sm outline-none resize-none"
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }} />
          <button onClick={send} disabled={busy} data-testid="mobile-chat-send"
                  className="p-2.5 rounded-full text-white disabled:opacity-50" style={{ background: C.red }}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
      </div>
      {showPeople && (
        <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}
             onClick={() => setShowPeople(false)}>
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2 max-h-[70vh] overflow-y-auto"
               style={{ background: C.card }} onClick={(ev) => ev.stopPropagation()}
               data-testid="mobile-chat-people">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">Empfänger</p>
              <button type="button" onClick={() => setShowPeople(false)} aria-label="Schließen">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            {(conv?.participants || []).map((p) => (
              <p key={p.id} className="text-sm py-1">{personName(p) || p.username}</p>
            ))}
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}
